// Rich medical content for services offered by Doctor Kichian's Gastroenterology.
// Each service includes: slug, name, tagline, icon (lucide name), short description,
// long-form sections (overview, benefits, ideal_for, what_to_expect, prep, faq).

export const SERVICES = [
    {
        slug: "colonoscopy",
        name: "Colonoscopy",
        tagline: "Comprehensive colon evaluation and cancer prevention.",
        icon: "Microscope",
        short:
            "A gentle, thorough exam of the colon using high-definition imaging — our most effective tool for detecting and preventing colon cancer.",
        overview:
            "Colonoscopy is a diagnostic and preventive procedure that allows a gastroenterologist to visually examine the entire lining of the colon and rectum using a slender, flexible tube with a high-definition camera. When precancerous polyps are found, they are typically removed during the same procedure — which is why colonoscopy is one of the few screening tests that both detects and prevents cancer.",
        benefits: [
            "Detects colon cancer at its earliest, most treatable stage",
            "Removes precancerous polyps before they can develop into cancer",
            "Evaluates causes of rectal bleeding, changes in bowel habits, or abdominal pain",
            "Personalized follow-up interval based on your findings and risk profile",
        ],
        ideal_for: [
            "Adults aged 45 and older due for routine screening",
            "Anyone with a family history of colon cancer or polyps",
            "Patients with unexplained changes in bowel habits, iron-deficiency anemia, or rectal bleeding",
            "Follow-up surveillance after prior polyp removal or inflammatory bowel disease",
        ],
        what_to_expect: [
            "Pre-procedure consultation to review your history and medications",
            "Prescribed bowel prep the day before (we provide clear, step-by-step instructions)",
            "Procedure typically 20–40 minutes under comfortable sedation",
            "Same-day discharge with a written report and next-step recommendations",
        ],
        prep_link: "/resources/preparing-for-colonoscopy",
    },
    {
        slug: "gastroscopy",
        name: "Gastroscopy (Upper Endoscopy)",
        tagline: "Precise evaluation of the esophagus, stomach, and duodenum.",
        icon: "ScanSearch",
        short:
            "A brief, well-tolerated exam of the upper digestive tract for reflux, ulcers, difficulty swallowing, and unexplained upper abdominal symptoms.",
        overview:
            "Gastroscopy — also called upper endoscopy or EGD — uses a thin flexible scope to inspect the lining of the esophagus, stomach, and the first portion of the small intestine. It is the most reliable way to diagnose acid-reflux related damage, Helicobacter pylori infection, ulcers, celiac disease, and Barrett's esophagus. Tissue samples (biopsies) can be taken painlessly during the procedure to confirm a diagnosis.",
        benefits: [
            "High-definition views uncover inflammation, ulcers, and precancerous changes",
            "Painless biopsies to test for celiac disease, H. pylori, and Barrett's esophagus",
            "Therapeutic capability — dilate strictures, treat bleeding, remove foreign bodies",
            "Fast, same-day recovery under gentle sedation",
        ],
        ideal_for: [
            "Persistent heartburn or GERD symptoms unresponsive to medication",
            "Difficulty or pain with swallowing (dysphagia)",
            "Unexplained upper abdominal pain, nausea, or weight loss",
            "Suspected celiac disease, H. pylori infection, or Barrett's esophagus surveillance",
        ],
        what_to_expect: [
            "Fasting from midnight prior to your procedure",
            "Procedure is typically completed in 10–20 minutes with sedation",
            "Written findings and biopsy results reviewed together at your follow-up",
            "Most patients return to normal activities the next day",
        ],
        prep_link: "/resources/preparing-for-gastroscopy",
    },
    {
        slug: "sigmoidoscopy",
        name: "Sigmoidoscopy",
        tagline: "A focused examination of the sigmoid portion of the colon.",
        icon: "Aperture",
        short:
            "A short, focused exam of the lower colon (the sigmoid) — ideal for evaluating rectal bleeding, changes in bowel habits, and lower left-sided abdominal symptoms.",
        overview:
            "Sigmoidoscopy is a short procedure performed at Queensway Carleton Hospital that uses a thin, flexible scope to examine the sigmoid portion of the colon — the last section of the large bowel before the rectum. Unlike a full colonoscopy, it looks only at the lower colon, so the preparation is simpler (two Fleet enemas rather than a full oral bowel prep). Sedation can be offered when clinically appropriate. The exam itself takes about 15 minutes, followed by up to an hour in the recovery area — expect approximately 2 hours at the hospital in total.",
        benefits: [
            "Clear, high-definition views of the sigmoid colon and rectum",
            "Simpler prep than a full colonoscopy — two Fleet enemas instead of oral bowel prep",
            "Short procedure (about 15 minutes) with same-day recovery",
            "Biopsies can be taken painlessly during the exam if needed",
        ],
        ideal_for: [
            "New rectal bleeding or blood on toilet paper",
            "Persistent changes in bowel habits affecting the lower colon",
            "Lower left-sided abdominal pain or discomfort",
            "Follow-up of known sigmoid diverticulosis or previous findings in the lower colon",
        ],
        what_to_expect: [
            "Clear fluids only after midnight the night before — no solids",
            "Two Fleet enemas at home before leaving for the hospital (available over the counter)",
            "Arrive at Admitting 1 hour before your appointment with your OHIP card and medication list",
            "The exam takes about 15 minutes, followed by up to 1 hour in recovery (~2 hours total at the hospital)",
            "If you choose sedation, you must have a responsible adult drive you home and cannot drive for 24 hours",
            "Please avoid perfume, cologne, or other scents on the day of your procedure (deodorant is fine)",
        ],
        prep_link: "/resources/preparing-for-sigmoidoscopy",
    },
    {
        slug: "digestive-health-consultation",
        name: "Digestive Health Consultations",
        tagline: "Time to be heard — comprehensive evaluations for complex GI concerns.",
        icon: "Stethoscope",
        short:
            "Unhurried, patient-centered consultations to understand your symptoms, review prior testing, and build a plan that fits your life.",
        overview:
            "Some digestive concerns don't fit neatly in a 10-minute appointment. Our consultation visits give you time to describe your symptoms, share prior test results, and ask questions. Dr. Kichian personally reviews your history, examines relevant lifestyle and dietary factors, and creates a shared roadmap for diagnosis and care.",
        benefits: [
            "Dedicated time to review complex or long-standing symptoms",
            "Second-opinion evaluations for patients seeking clarity",
            "Integrated review of medications, diet, mental health, and lifestyle contributors",
            "Personalized diagnostic plan — no cookie-cutter workups",
        ],
        ideal_for: [
            "Chronic or recurring digestive symptoms without a clear diagnosis",
            "Complex conditions such as IBD, IBS, or overlapping syndromes",
            "Patients seeking a second opinion or care coordination",
            "Anyone whose symptoms are affecting quality of life",
        ],
        prep_link: "/resources/preparing-for-your-visit",
    },
    {
        slug: "colon-cancer-screening",
        name: "Colon Cancer Screening",
        tagline: "Prevent tomorrow's cancer with today's screening.",
        icon: "ShieldCheck",
        short:
            "Age- and risk-based colorectal cancer screening with clear guidance on which test is right for you — colonoscopy, stool-based, or hybrid strategies.",
        overview:
            "Colon cancer is one of the most preventable cancers because it typically develops from polyps that grow slowly and can be identified and removed before they become cancerous. Screening saves lives — and we help you choose the right strategy based on your age, personal history, family history, and preferences.",
        benefits: [
            "Personalized recommendations based on age, risk factors, and lifestyle",
            "Options include colonoscopy and FIT (fecal immunochemical) testing",
            "Clear timelines for repeat screening based on your results",
            "Family risk assessment and screening for first-degree relatives when appropriate",
        ],
        ideal_for: [
            "Adults 45+ who have never been screened",
            "Anyone with a first-degree relative diagnosed with colon cancer or advanced polyps",
            "Patients with hereditary syndromes such as Lynch syndrome or FAP",
            "Patients due for surveillance after prior polyp removal",
        ],
        what_to_expect: [
            "A brief risk assessment and shared decision on screening approach",
            "Prep and scheduling coordination with our team",
            "Clear written results and next-screening date",
            "Ongoing reminders so you never miss a follow-up",
        ],
        prep_link: "/resources/preparing-for-colonoscopy",
    },
    {
        slug: "liver-health-assessment",
        name: "Liver Health Assessments",
        tagline: "Understand and protect your liver — before symptoms appear.",
        icon: "HeartPulse",
        short:
            "Comprehensive assessments for fatty liver disease, viral hepatitis, and abnormal liver tests — with practical steps to protect long-term liver health.",
        overview:
            "Liver disease often develops silently. Modern non-invasive tools — elastography, lab profiling, and imaging — allow us to detect fatty liver disease, fibrosis, and other liver conditions early, when lifestyle and medical interventions can meaningfully change the trajectory.",
        benefits: [
            "Non-invasive fibrosis assessment (elastography) when indicated",
            "Structured evaluation of abnormal liver enzymes",
            "Personalized nutrition and lifestyle plan for fatty liver disease",
            "Coordination for hepatitis B/C evaluation and monitoring",
        ],
        ideal_for: [
            "Patients with elevated liver enzymes on routine blood work",
            "Anyone with metabolic risk factors (obesity, diabetes, high cholesterol)",
            "Chronic hepatitis B or C surveillance",
            "Family history of liver disease or unexplained fatigue",
        ],
        what_to_expect: [
            "Focused history and physical examination",
            "Targeted labs and non-invasive fibrosis assessment when appropriate",
            "Personalized lifestyle plan (nutrition, activity, alcohol guidance)",
            "Follow-up schedule aligned with your risk category",
        ],
        prep_link: "/resources/preparing-for-your-visit",
    },
    {
        slug: "gerd-management",
        name: "Acid Reflux & GERD Management",
        tagline: "Real relief from heartburn, regurgitation, and reflux symptoms.",
        icon: "Flame",
        short:
            "A structured approach to GERD — lifestyle, medication optimization, and endoscopic evaluation — so you can eat, sleep, and live comfortably again.",
        overview:
            "Gastroesophageal reflux disease (GERD) can significantly reduce quality of life and, when untreated, may damage the esophagus. We take a step-by-step approach: understand your triggers, optimize medications you may already be on, and use endoscopy when needed to rule out complications such as Barrett's esophagus.",
        benefits: [
            "Reduce or eliminate heartburn and regurgitation",
            "Identify hidden triggers (diet, timing, sleep position, weight)",
            "Rule out complications such as strictures or Barrett's esophagus",
            "Medication de-prescribing when possible — not everyone needs long-term PPIs",
        ],
        ideal_for: [
            "Persistent heartburn or regurgitation despite over-the-counter treatment",
            "Nighttime reflux affecting sleep",
            "Chronic cough, hoarseness, or throat clearing suspected to be reflux-related",
            "Patients wanting to reduce long-term acid-suppression medications",
        ],
        what_to_expect: [
            "Detailed symptom and lifestyle review",
            "Endoscopy when indicated, plus symptom-based testing",
            "Tailored plan including diet timing, weight, medications, and sleep positioning",
            "Follow-up to confirm improvement and adjust the plan",
        ],
        prep_link: "/resources/preparing-for-your-visit",
    },
    {
        slug: "ibd-care",
        name: "Inflammatory Bowel Disease Care",
        tagline: "Comprehensive Crohn's disease and ulcerative colitis management.",
        icon: "Activity",
        short:
            "Long-term partnership for Crohn's disease and ulcerative colitis — modern therapies, monitoring, and support to keep you in remission and living fully.",
        overview:
            "Inflammatory bowel disease (IBD) is a lifelong condition, but with modern treatments most patients can achieve and maintain deep, durable remission. We provide comprehensive IBD care — diagnostic confirmation, medication selection, biologic therapy coordination, disease-activity monitoring, and preventive health — tailored to your goals.",
        benefits: [
            "Precision therapy — mesalamines, biologics, small molecules, and combination approaches",
            "Regular monitoring using biomarkers (fecal calprotectin) and imaging",
            "Nutrition, mental health, and bone-health coordination",
            "Preventive care: vaccines, cancer surveillance, and family planning discussions",
        ],
        ideal_for: [
            "New IBD diagnoses seeking a structured plan",
            "Flares requiring re-evaluation and treatment escalation",
            "Family planning while living with IBD",
        ],
        what_to_expect: [
            "Detailed disease-mapping using labs, imaging, and endoscopy",
            "Shared decision-making around therapy choices",
            "Structured monitoring schedule — not just when you feel unwell",
            "Coordinated care with your primary team",
        ],
        prep_link: "/resources/preparing-for-your-visit",
    },
    {
        slug: "ibs-management",
        name: "Irritable Bowel Syndrome (IBS) Management",
        tagline: "Understanding your gut — without labels or shortcuts.",
        icon: "Sparkles",
        short:
            "A modern, multidisciplinary approach to IBS — combining diagnosis, dietary strategies, brain–gut therapies, and lifestyle changes tailored to your subtype.",
        overview:
            "IBS affects millions of people and can significantly impact daily life. We take IBS seriously — confirming the diagnosis, ruling out overlapping conditions when appropriate, and building an evidence-based plan that combines diet (including low-FODMAP when suitable), medications, stress management, and gut-directed therapies.",
        benefits: [
            "Confirm the diagnosis and identify subtype (IBS-D, IBS-C, IBS-M)",
            "Structured dietary strategies including low-FODMAP guidance when appropriate",
            "Symptom-targeted medications and gut-directed therapies",
        ],
        ideal_for: [
            "Persistent abdominal pain, bloating, or altered bowel habits",
            "Symptom overlap with food sensitivities, anxiety, or fatigue",
        ],
        what_to_expect: [
            "In-depth history focused on triggers, timing, and severity",
            "Focused testing to rule out overlapping conditions",
            "Personalized dietary and medication plan",
            "Follow-up to review progress and refine the plan",
        ],
        prep_link: "/resources/preparing-for-your-visit",
    },
];

export function getServiceBySlug(slug) {
    return SERVICES.find((s) => s.slug === slug);
}
